/**
 * Build optimizer queries and mutations. `useBuild` polls while a build is queued or
 * generating, the same way the addon upload page follows a processing upload.
 */
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { BuildCreateResponse, BuildGearSelectionBody, BuildOptionsResponse, BuildRequest, BuildView } from '@thottbot/shared';
import { apiGet, apiSend, queryKeys, type ApiError } from './client';

export function useBuildOptions(): UseQueryResult<BuildOptionsResponse, ApiError> {
  return useQuery({
    queryKey: queryKeys.buildOptions,
    queryFn: () => apiGet<BuildOptionsResponse>('/api/builds/options'),
    staleTime: 30_000,
  });
}

/** Follows one build; stops polling once it reaches a terminal status. */
export function useBuild(id: string | null): UseQueryResult<BuildView, ApiError> {
  return useQuery({
    queryKey: queryKeys.build(id ?? ''),
    queryFn: () => apiGet<BuildView>(`/api/builds/${id}`),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.status === 'queued' || q.state.data?.status === 'generating' ? 2000 : false),
    refetchIntervalInBackground: true,
    retry: false,
  });
}

export function useCreateBuild(): UseMutationResult<BuildCreateResponse, ApiError, BuildRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: BuildRequest) => apiSend<BuildCreateResponse>('POST', '/api/builds', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.buildOptions });
      void client.invalidateQueries({ queryKey: queryKeys.myBuilds });
    },
  });
}

export function useRegenerateBuild(): UseMutationResult<BuildCreateResponse, ApiError, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiSend<BuildCreateResponse>('POST', `/api/builds/${id}/regenerate`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.buildOptions });
      void client.invalidateQueries({ queryKey: queryKeys.myBuilds });
    },
  });
}

export function useMyBuilds(enabled: boolean): UseQueryResult<BuildView[], ApiError> {
  return useQuery({
    queryKey: queryKeys.myBuilds,
    queryFn: () => apiGet<BuildView[]>('/api/me/builds'),
    enabled,
  });
}

/** The owner wears another of a slot's options; the fresh build view replaces the cached one. */
export function useSetGearSelection(id: string): UseMutationResult<BuildView, ApiError, BuildGearSelectionBody> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: BuildGearSelectionBody) => apiSend<BuildView>('PATCH', `/api/builds/${id}/gear`, body),
    onSuccess: (view) => client.setQueryData(queryKeys.build(id), view),
  });
}

/** Saves (`saved: true`) or unsaves a build into the viewer's "Your builds" list. */
export function useSaveBuild(): UseMutationResult<void, ApiError, { id: string; saved: boolean }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, saved }) => apiSend<void>(saved ? 'PUT' : 'DELETE', `/api/builds/${id}/save`),
    onSuccess: (_res, { id }) => {
      void client.invalidateQueries({ queryKey: queryKeys.build(id) });
      void client.invalidateQueries({ queryKey: queryKeys.myBuilds });
    },
  });
}
